// App-shell producer for the dashboard renderer's `hostValues` channel (ops-dashboard-config-
// migration FR4, ledger row 26).
//
// WHY THIS EXISTS AT ALL. `KPICardLive`'s headline is the gym's live headcount, and it arrives over
// a dGraph WebSocket SUBSCRIPTION — a React context fed by a socket, with no endpoint, no URL and
// nothing for `DashboardDataProvider` (whose entire contract is `fetch(binding, context, signal)`)
// to call. A definition cannot express that binding, and teaching the provider a second transport
// for one panel would put a subscription into the binding vocabulary permanently, for every
// dashboard (OM-3). So the definition NAMES the reading it wants — `liveValue: "countEntity"` — and
// the host supplies the map.
//
// ⚠️ THE HEADLINE ONLY. The interim `useLiveOccupancy` this replaces also fetched a baseline and
// divided to produce "% of typical max". That computation is DELETED, not moved: BI owns the rule
// and serves `pct_of_typical_peak` on the `/api/now` row (Peter, 2026-08-20; AC13). A wrong figure
// there is a defect to fix in BI, never grounds to reinstate the client's own arithmetic.
//
// `null` is meaningful and is not the same as an absent key: it means this host OWNS the reading
// and has none right now (a closed gym, a dropped socket), so the card shows its empty treatment
// rather than a stale number dressed up as live. The key is always present in the returned map for
// that reason.
import { useMemo } from 'react';
import { DataMode } from '@repo/frontend-types';
import { getDataValue } from '@repo/front-end-utils';
import type { HostValues } from '@repo/ui';
// 🚨 IMPORTED FROM `@repo/front-end-state` DIRECTLY, not through the local `DataContext` module —
// which re-exports this same hook on its line 34 and is therefore type-correct to import from.
// `tsc --noEmit` accepts either. WEBPACK DOES NOT: it cannot follow a re-export through the
// package's CJS `dist`, and reports `useDataContext` as not found with "possible exports:
// DataContextProvider". So the first spelling type-checks green, passes CI's `check-types`, and
// then fails the dev build — which is the only place it shows up. The deleted `useOpsData.ts`
// imported it this way for the same reason; keep it.
import { useDataContext, useTwinContext } from '@repo/front-end-state';

/** The metric name the dGraph LIVE subscription publishes the site headcount under. */
const LIVE_HEADCOUNT_METRIC = 'countEntity';

export const useOpsHostValues = (): HostValues => {
  const { data } = useDataContext();
  const { twin } = useTwinContext();

  const liveRows = data.processed?.[LIVE_HEADCOUNT_METRIC];
  const siteId = twin?.physicalModel?.id;

  return useMemo(() => {
    let headcount: number | null = null;
    if (liveRows && siteId) {
      const siteValue = getDataValue(DataMode.LIVE, liveRows, siteId);
      headcount = typeof siteValue?.value === 'number' ? siteValue.value : null;
    }
    // Memoised because this map is a prop on the renderer and reaches every component: a fresh
    // object each render would give every wrapper a new `hostValues` identity on every tick of an
    // unrelated subscription.
    return { [LIVE_HEADCOUNT_METRIC]: headcount };
  }, [liveRows, siteId]);
};
